import type { ReactElement } from 'react';
import { Activity, CircleCheck, Clock, X, Zap } from 'lucide-react';

import { useTranslation } from '@govard/i18n-core/react';

interface RightSidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

export function RightSidebar({ isOpen, onClose }: RightSidebarProps): ReactElement | null {
  const { t } = useTranslation();

  if (!isOpen) {
    return null;
  }

  return (
    <aside
      className="hidden h-full shrink-0 flex-col border-l lg:flex"
      data-testid="crm-right-sidebar"
      aria-label={t('crm.right_sidebar.title')}
      style={{
        width: 280,
        backgroundColor: 'var(--color-bg-secondary, #f5f1eb)',
        borderColor: 'var(--color-border, #ddd7cc)',
        animation: 'slideInRight 0.2s ease-out forwards',
      }}
    >
      <div
        className="flex shrink-0 items-center justify-between border-b"
        style={{
          height: 'var(--header-height, 56px)',
          padding: '0 20px',
          borderColor: 'var(--color-border, #ddd7cc)',
        }}
      >
        <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text, #2d2418)' }}>
          {t('crm.right_sidebar.title')}
        </h2>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 transition-colors hover:bg-[var(--color-surface-hover,#f0ece5)] hover:text-[var(--color-text,#2d2418)]"
            style={{ color: 'var(--color-text-muted, #a89b8a)' }}
            aria-label={t('crm.right_sidebar.close')}
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      <div className="hide-scrollbar flex-1 overflow-y-auto" style={{ padding: 20 }}>
        <p className="text-xs" style={{ color: 'var(--color-text-secondary, #6b5e4f)' }}>
          {t('crm.right_sidebar.description')}
        </p>

        <section className="mt-5">
          <h3
            className="text-[10px] font-bold uppercase tracking-[0.08em]"
            style={{ color: 'var(--color-text-muted, #a89b8a)', marginBottom: 12 }}
          >
            {t('crm.right_sidebar.system_status')}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <MetricCard
              icon={<Activity size={14} />}
              label={t('crm.right_sidebar.block_status')}
              value={t('crm.right_sidebar.status_online')}
              tone="success"
            />
            <MetricCard
              icon={<Zap size={14} />}
              label={t('crm.right_sidebar.block_scope')}
              value={t('crm.right_sidebar.scope_workspace')}
              tone="warning"
            />
          </div>
        </section>

        <section className="mt-6">
          <h3
            className="text-[10px] font-bold uppercase tracking-[0.08em]"
            style={{ color: 'var(--color-text-muted, #a89b8a)', marginBottom: 12 }}
          >
            {t('crm.right_sidebar.recent_activity')}
          </h3>
          <div className="flex flex-col gap-3">
            <ActivityRow
              icon={<CircleCheck size={10} />}
              tone="success"
              title={t('crm.right_sidebar.activity_visual_contract')}
              time={t('crm.right_sidebar.activity_now')}
            />
            <ActivityRow
              icon={<Clock size={10} />}
              tone="info"
              title={t('crm.right_sidebar.activity_next_slice')}
              time={t('crm.right_sidebar.activity_next')}
            />
          </div>
        </section>
      </div>
    </aside>
  );
}

function MetricCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: 'success' | 'warning';
}): ReactElement {
  const color =
    tone === 'success' ? 'var(--color-success, #5a9a62)' : 'var(--color-warning, #c49322)';
  return (
    <div
      className="rounded-lg border"
      style={{
        padding: 12,
        backgroundColor: 'var(--color-surface, #faf8f5)',
        borderColor: 'var(--color-border, #ddd7cc)',
      }}
    >
      <div style={{ color, marginBottom: 8 }}>{icon}</div>
      <div className="text-[11px]" style={{ color: 'var(--color-text-muted, #a89b8a)' }}>
        {label}
      </div>
      <div
        className="mt-1 truncate text-xs font-semibold"
        style={{ color: 'var(--color-text, #2d2418)' }}
      >
        {value}
      </div>
    </div>
  );
}

function ActivityRow({
  icon,
  tone,
  title,
  time,
}: {
  icon: React.ReactNode;
  tone: 'success' | 'info';
  title: string;
  time: string;
}): ReactElement {
  const color = tone === 'success' ? 'var(--color-success, #5a9a62)' : 'var(--color-info, #5a7aac)';
  return (
    <div className="flex items-center" style={{ gap: 14 }}>
      <div
        className="flex shrink-0 items-center justify-center rounded-full border-2"
        style={{
          width: 20,
          height: 20,
          backgroundColor: 'var(--color-bg-secondary, #f5f1eb)',
          borderColor: color,
          color,
        }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs" style={{ color: 'var(--color-text, #2d2418)' }}>
          {title}
        </p>
        <p className="text-[10px]" style={{ color: 'var(--color-text-muted, #a89b8a)' }}>
          {time}
        </p>
      </div>
    </div>
  );
}
